// UTM (Urchin Tracking Module) attribution tracking
import { getISTTime } from "../constants.js";

// Standard UTM parameters plus common ad-click identifiers.
const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];
const CLICK_ID_PARAMS = ["gclid", "fbclid"];

/**
 * Snapshot of UTM params + URL taken at MODULE LOAD time — i.e. the instant the
 * SDK script is parsed, before any SDK instance is created and before an SPA
 * router can rewrite/strip the query string. The SDK reads from this snapshot
 * so UTMs are never lost to a late initialization or early route change.
 */
export const EARLY_UTM_SNAPSHOT = (() => {
  try {
    if (typeof window === "undefined" || !window.location) {
      return { params: {}, landingPage: "", referrer: "" };
    }
    return {
      params: _parseUtm(window.location.search),
      landingPage: window.location.href,
      referrer: (typeof document !== "undefined" && document.referrer) || "",
    };
  } catch (e) {
    return { params: {}, landingPage: "", referrer: "" };
  }
})();

/**
 * Internal parser shared by parseUtmParams and the early snapshot.
 */
function _parseUtm(search) {
  const result = {};
  try {
    const params = new URLSearchParams(search || "");
    for (const key of UTM_PARAMS) {
      const val = params.get(key);
      if (val) result[key] = val;
    }
    for (const key of CLICK_ID_PARAMS) {
      const val = params.get(key);
      if (val) result[key] = val;
    }
  } catch (e) {
    // Malformed query string — return whatever was parsed
  }
  return result;
}

/**
 * Parses UTM and click-id parameters from a URL query string.
 * @param {string} search - The location.search string (e.g. "?utm_source=google").
 * @returns {Object} Object with any UTM/click-id params found (empty if none).
 */
export function parseUtmParams(search) {
  return _parseUtm(search);
}

/**
 * Manages first-touch and last-touch UTM attribution.
 * - first_touch: captured once on the user's very first visit, never overwritten.
 * - last_touch: refreshed whenever new UTM params appear in the URL.
 *
 * Both are persisted in localStorage, scoped by appId.
 */
export class UtmTracker {
  /**
   * @param {string} appId - Used to scope localStorage keys.
   */
  constructor(appId = "default") {
    this.firstTouchKey = `dhan_utm_first_${appId}`;
    this.lastTouchKey = `dhan_utm_last_${appId}`;
  }

  /**
   * Captures UTM params from the current URL.
   * Updates first-touch (if not already set) and last-touch (if new params present).
   * @param {string} search - location.search
   * @param {string} landingPage - full landing URL
   * @param {string} referrer - document.referrer
   * @returns {Object|null} A capture record if NEW UTMs were detected this call,
   *                        otherwise null. Used to decide whether to fire an event.
   */
  /**
   * Captures using the module-load-time snapshot (EARLY_UTM_SNAPSHOT).
   * Prefer this over capture() so UTMs read at script-load are used even if an
   * SPA router has since rewritten the URL.
   * @returns {Object|null} Same shape as capture().
   */
  captureFromSnapshot() {
    return this.capture(
      // Rebuild a search string from the snapshot params so capture() can parse it.
      "?" + new URLSearchParams(EARLY_UTM_SNAPSHOT.params).toString(),
      EARLY_UTM_SNAPSHOT.landingPage,
      EARLY_UTM_SNAPSHOT.referrer
    );
  }

  capture(search, landingPage, referrer) {
    const params = parseUtmParams(search);

    // No UTM params in URL — nothing new to capture this visit.
    if (Object.keys(params).length === 0) {
      return null;
    }

    const now = getISTTime();
    const record = {
      ...params,
      landing_page: landingPage || "",
      referrer: referrer || "",
      captured_at: now,
    };

    let isNewFirstTouch = false;

    try {
      if (typeof localStorage !== "undefined") {
        // First-touch: only set if not already present.
        if (!localStorage.getItem(this.firstTouchKey)) {
          localStorage.setItem(this.firstTouchKey, JSON.stringify(record));
          isNewFirstTouch = true;
        }
        // Last-touch: always overwrite with the latest campaign.
        localStorage.setItem(this.lastTouchKey, JSON.stringify(record));
      }
    } catch (e) {
      // localStorage unavailable — attribution works for this session only
    }

    return {
      params: record,
      isNewFirstTouch,
    };
  }

  /**
   * Returns the stored first-touch attribution, or null.
   */
  getFirstTouch() {
    return this._read(this.firstTouchKey);
  }

  /**
   * Returns the stored last-touch attribution, or null.
   */
  getLastTouch() {
    return this._read(this.lastTouchKey);
  }

  /**
   * Returns combined attribution context for inclusion in events.
   * @returns {Object} { first_touch, last_touch } (either may be null).
   */
  getAttribution() {
    return {
      first_touch: this.getFirstTouch(),
      last_touch: this.getLastTouch(),
    };
  }

  /**
   * Returns a flat, lightweight attribution reference for inclusion in every
   * batch — just source / medium / campaign from the most recent (last-touch)
   * attribution. Keeps the batch payload small.
   * @returns {Object|null} { source, medium, campaign } or null if no attribution.
   */
  getAttributionRef() {
    const last = this.getLastTouch();
    if (!last) return null;

    return {
      source: last.utm_source || null,
      medium: last.utm_medium || null,
      campaign: last.utm_campaign || null,
    };
  }

  _read(key) {
    try {
      if (typeof localStorage === "undefined") return null;
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
}
